import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// Top-level sidebar item ids that the user is not allowed to hide.
// Without Home/Settings the chooser becomes unreachable; Dashboard remains
// hideable in favor of Home but stays reachable via direct URL.
export const NON_HIDEABLE_SIDEBAR_IDS = new Set<string>(['home', 'settings']);

export type StartPage = 'home' | 'dashboard';

/**
 * L06: where a signed-in user starts. The dashboard opens for MANAGER (30) and
 * up, so below that the saved choice is ignored (not erased) and Home is used —
 * a refused route redirects to the start page, so this must never be /dashboard
 * for a cashier.
 */
export function resolveStartPath(page: StartPage, roleLevel: number): '/home' | '/dashboard' {
  return page === 'dashboard' && roleLevel >= 30 ? '/dashboard' : '/home';
}

const MAX_RECENT_PATHS = 5;

const STORAGE_NAME = 'bbpos-admin-sidebar-prefs:v1';

// Preferences belong to the signed-in user, not the browser: on a shared
// terminal a second user must not inherit the first user's hidden modules.
// The storage key carries the user id; signed out, the bare key is used.
let activeUserId: string | null = null;
const scopedKey = (name: string) => (activeUserId ? `${name}:${activeUserId}` : name);

interface SidebarPrefsState {
  hiddenIds: string[];
  startPage: StartPage;
  recentPaths: string[];
  /** Persisted collapse state — survives reloads like the other prefs here. */
  collapsed: boolean;
  /**
   * True once the SUPER_ADMIN collapsed-by-default has been applied, so a user
   * who deliberately expands the sidebar is not re-collapsed on every load.
   */
  collapsedDefaultApplied: boolean;
  /** Sidebar section groups the user has expanded, by group label — all start collapsed. */
  expandedGroups: string[];

  toggle: (id: string) => void;
  hide: (id: string) => void;
  show: (id: string) => void;
  setHidden: (ids: string[]) => void;
  reset: () => void;

  setStartPage: (page: StartPage) => void;
  recordVisit: (path: string) => void;
  clearRecents: () => void;

  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  applyCollapsedDefault: () => void;
  setExpandedGroups: (groups: string[]) => void;
}

export const useSidebarPrefsStore = create<SidebarPrefsState>()(
  persist(
    (set, get) => ({
      hiddenIds: [],
      startPage: 'home',
      recentPaths: [],
      collapsed: false,
      collapsedDefaultApplied: false,
      expandedGroups: [],

      toggle: (id) => {
        if (NON_HIDEABLE_SIDEBAR_IDS.has(id)) return;
        const s = new Set(get().hiddenIds);
        if (s.has(id)) s.delete(id);
        else s.add(id);
        set({ hiddenIds: Array.from(s) });
      },
      hide: (id) => {
        if (NON_HIDEABLE_SIDEBAR_IDS.has(id)) return;
        const s = new Set(get().hiddenIds);
        s.add(id);
        set({ hiddenIds: Array.from(s) });
      },
      show: (id) => {
        set({ hiddenIds: get().hiddenIds.filter((x) => x !== id) });
      },
      setHidden: (ids) =>
        set({ hiddenIds: ids.filter((id) => !NON_HIDEABLE_SIDEBAR_IDS.has(id)) }),
      reset: () => set({ hiddenIds: [] }),

      setStartPage: (page) => set({ startPage: page }),
      recordVisit: (path) => {
        // Skip recording the launchpad itself — would always sit on top.
        if (path === '/home' || path === '/' || path === '/login') return;
        const current = get().recentPaths.filter((p) => p !== path);
        current.unshift(path);
        set({ recentPaths: current.slice(0, MAX_RECENT_PATHS) });
      },
      clearRecents: () => set({ recentPaths: [] }),

      setCollapsed: (collapsed) => set({ collapsed }),
      toggleCollapsed: () => set({ collapsed: !get().collapsed }),
      applyCollapsedDefault: () => {
        if (get().collapsedDefaultApplied) return;
        set({ collapsed: true, collapsedDefaultApplied: true });
      },
      setExpandedGroups: (groups) => set({ expandedGroups: groups }),
    }),
    {
      name: STORAGE_NAME,
      storage: createJSONStorage(() => ({
        getItem: (name) => localStorage.getItem(scopedKey(name)),
        setItem: (name, value) => localStorage.setItem(scopedKey(name), value),
        removeItem: (name) => localStorage.removeItem(scopedKey(name)),
      })),
      partialize: (s) => ({
        hiddenIds: s.hiddenIds,
        startPage: s.startPage,
        recentPaths: s.recentPaths,
        collapsed: s.collapsed,
        collapsedDefaultApplied: s.collapsedDefaultApplied,
        expandedGroups: s.expandedGroups,
      }),
    },
  ),
);

const DEFAULT_PREFS = {
  hiddenIds: [],
  startPage: 'home' as StartPage,
  recentPaths: [],
  collapsed: false,
  collapsedDefaultApplied: false,
  expandedGroups: [],
};

/** Switch the store to another user's saved preferences (defaults if none). */
export function bindSidebarPrefsToUser(userId: string | null): void {
  if (userId === activeUserId) return;
  activeUserId = userId;
  // Every field is persisted, so rehydrating a saved entry replaces the whole
  // state; with no saved entry rehydrate is a no-op and the previous user's
  // prefs would linger, so fall back to the defaults explicitly.
  if (localStorage.getItem(scopedKey(STORAGE_NAME)) === null) {
    useSidebarPrefsStore.setState(DEFAULT_PREFS);
  } else {
    void useSidebarPrefsStore.persist.rehydrate();
  }
}
